import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, LayoutGrid, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { classNames } from '../../utils/classNames';
import { useI18n } from '../../hooks/useI18n';
import {
  getModelByKey,
  getQuickModels,
  storeSelectedModel
} from '../../constants/chatImageModels';
import { useLlmCatalog } from '../../hooks/useLlmCatalog';
import { useMyPackage } from '../../hooks/useMyPackage';
import { UpgradePlanButton } from '../../components/layout/UpgradePlanButton';

export const ModelQuickDropdown = ({
  kind,
  modelKey,
  onChange,
  returnTo,
  className = '',
  menuAlign = 'start'
}) => {
  const { t, language } = useI18n();
  const { models: catalogModels } = useLlmCatalog();
  const entitlement = useMyPackage();
  const showUpgrade = Boolean(entitlement) && !entitlement.hasPackage;
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const models = getQuickModels(kind, catalogModels);
  const active = getModelByKey(kind, modelKey, catalogModels);
  const modelsPage = `/models/${kind}?return=${encodeURIComponent(returnTo || (kind === 'image' ? '/images' : '/chat'))}`;

  useEffect(() => {
    const onPointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, []);

  const selectModel = (model) => {
    if (model.allowed === false) {
      navigate('/pricing');
      setOpen(false);
      return;
    }
    storeSelectedModel(kind, model.key);
    onChange?.(model.key);
    setOpen(false);
  };

  return (
    <div ref={rootRef} className={classNames('relative', className)}>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="inline-flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-sm font-semibold text-neutral-900 transition hover:bg-neutral-100 dark:text-text dark:hover:bg-accent-soft"
      >
        <span className="max-w-[180px] truncate">{active?.name}</span>
        <ChevronDown size={14} className={classNames('text-neutral-400 transition', open && 'rotate-180')} />
      </button>

      {open ? (
        <div
          className={classNames(
            'absolute z-50 mt-2 w-[320px] overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-[0_12px_40px_rgba(0,0,0,0.08)] dark:border-border dark:bg-panel dark:shadow-[0_16px_40px_rgba(0,0,0,0.4)]',
            menuAlign === 'end' ? 'end-0' : 'start-0'
          )}
        >
          {showUpgrade ? (
            <div className="flex items-center justify-between gap-3 border-b border-neutral-100 px-4 py-3 dark:border-border">
              <div className="min-w-0">
                <div className="text-sm font-semibold text-neutral-900 dark:text-text">{t('modelUpgradeTitle')}</div>
                <div className="truncate text-xs text-neutral-400 dark:text-muted">{t('modelUpgradeHint')}</div>
              </div>
              <UpgradePlanButton compact />
            </div>
          ) : null}

          <ul className="py-1">
            {models.map((model) => {
              const isActive = model.key === modelKey;
              return (
                <li key={model.key}>
                  <button
                    type="button"
                    onClick={() => selectModel(model)}
                    className={classNames(
                      'flex w-full items-start gap-3 px-4 py-3 text-start transition',
                      isActive ? 'bg-neutral-50 dark:bg-accent-soft' : 'hover:bg-neutral-50 dark:hover:bg-white/[0.04]'
                    )}
                  >
                    <span className="mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-neutral-100 text-neutral-700 dark:bg-accent-soft dark:text-accent">
                      <Sparkles size={14} />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span
                        className={classNames(
                          'block text-sm font-semibold',
                          isActive ? 'text-emerald-600 dark:text-emerald-400' : 'text-neutral-900 dark:text-text'
                        )}
                      >
                        {model.name}
                        {model.allowed === false ? (
                          <span className="ms-2 text-[10px] font-medium text-neutral-400">{t('modelLocked')}</span>
                        ) : null}
                      </span>
                      <span className="mt-0.5 block text-xs leading-5 text-neutral-400">
                        {language === 'fa' ? model.descriptionFa : model.descriptionEn}
                      </span>
                    </span>
                  </button>
                </li>
              );
            })}
            <li>
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  navigate(modelsPage);
                }}
                className="flex w-full items-start gap-3 px-4 py-3 text-start transition hover:bg-neutral-50 dark:hover:bg-white/[0.04]"
              >
                <span className="mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-neutral-100 text-neutral-700 dark:bg-accent-soft dark:text-accent">
                  <LayoutGrid size={14} />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-semibold text-neutral-900 dark:text-text">{t('selectOtherModels')}</span>
                  <span className="mt-0.5 block text-xs leading-5 text-neutral-400">{t('selectOtherModelsHint')}</span>
                </span>
              </button>
            </li>
          </ul>
        </div>
      ) : null}
    </div>
  );
};
